import { css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import type { Key, ReactNode } from 'react';
import { useRef, useState } from 'react';

import IconAddFilled from '~icons/carbon/add-filled';

import { su, theme } from '@adflow/frontend/ui-theme';

import { Callout } from './Callout';
import { Menu, MenuButton, MenuItem, MenuList } from './MenuButton';

const styles = {
	addButton: css(su.textXl, {
		color: theme.colors.text.tableHeader,
		alignSelf: 'center',
		cursor: 'pointer',
		'&[data-disabled=true]': css({
			color: theme.colors.action.disabledBackground,
			cursor: 'default',
		}),
	}),
	addButtonIcon: css({
		height: '1.625rem',
		width: '1.625rem',
	}),
};

export interface AddMenuOption {
	key: Key;
	label: ReactNode;
	onClick: () => void;
}

export interface AddMenuProps {
	addOptions: AddMenuOption[];
	addButtonStyles?: CSSInterpolation;
	buttonIconStyles?: CSSInterpolation;
	label?: string;
	listTestid?: string;
	disabled?: boolean;
	disabledCalloutText?: string | ReactNode;
}

export function AddMenu({
	addOptions,
	addButtonStyles,
	buttonIconStyles,
	label,
	listTestid,
	disabled,
	disabledCalloutText,
}: AddMenuProps) {
	const [showingCallout, setShowingCallout] = useState(false);
	const buttonRef = useRef<HTMLButtonElement | null>(null);

	return (
		<>
			<Menu
				trigger={
					<MenuButton
						css={[styles.addButton, addButtonStyles]}
						data-testid="add-menu-button"
						// Use data-disabled because disabled blocks event handlers
						data-disabled={disabled}
						onClick={(e) => {
							if (disabled) {
								setShowingCallout(true);
								e.preventDefault();
								e.stopPropagation();
								return;
							}
						}}
						onMouseLeave={() => setShowingCallout(false)}
						ref={buttonRef}
					>
						{label}
						<IconAddFilled css={[styles.addButtonIcon, buttonIconStyles]} />
					</MenuButton>
				}
			>
				<MenuList data-testid={listTestid}>
					{addOptions.map((option) => (
						<MenuItem
							key={option.key}
							onSelect={option.onClick}
							data-testid="add-menu-item"
						>
							{option.label}
						</MenuItem>
					))}
				</MenuList>
			</Menu>
			{showingCallout && disabledCalloutText ? (
				<Callout targetRef={buttonRef}>{disabledCalloutText}</Callout>
			) : null}
		</>
	);
}
